import APopover from 'components/APopover';
import CKEditorField from 'components/CKEditorField';
import { CKEditorReadOnlyConfig } from 'components/CKEditorField/CKEditorField';
import { getEffectiveName } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/UniqueQuestion/PeriodTypeSelector/DatePeriod';
import { convertTimeToString } from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/AnswerView';
import HoverIcon from 'components/HoverIcon';
import Icon from 'components/Icon';
import { withTitle } from 'components/withTitle';
import { TaskAttachmentsModal } from 'containers/Tasks/TasksManagement/TaskAttachments/Modal/TaskAttachmentsModal';
import i18n, { getCalendarLocal } from 'i18n';
import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ITags } from 'store/providers/providerTypes';
import SanitizedHTML from 'react-sanitized-html';
import { RootState } from 'store/store';
import {
  getTimeScale,
  IAnswersView,
  IChoiceView,
  IFileAnswerView,
  IIndicatorAnswerView,
  IIndicatorQuestionView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISurveyResponseView,
  ISurveyViewWithData,
  ITextAnswerView,
  ITextQuestionView,
  TimeScale,
} from 'store/survey/surveyTypes';
import { getPureTextFromHtml } from 'store/tasks/taskManipulate';
import LocalIcon from 'ui/LocalIcon';
import { checkFileFormat } from 'utils/check-file-type/checkFileFormat';
import { smartDateString } from 'utils/date';
import {
  convertDigitToProperFormat,
  convertNumbersToProperFormat,
  englishNumber,
  isANumber,
} from 'utils/number';
import { black, blue, exactWhite } from 'utils/style/colors';

interface IQuestionListReportProps {
  data: any;
  survey: ISurveyViewWithData;
  questionIdList: string[];
  tags: ITags;
}

const questionFields = [
  { value: 'submitDate', label: i18n.t('submitDate', { ns: 'survey' }) },
  { value: 'score', label: i18n.t('score', { ns: 'survey' }) },
  { value: 'relatedUser', label: i18n.t('relatedUser', { ns: 'survey' }) },
  { value: 'userId', label: i18n.t('userId', { ns: 'survey' }) },
  { value: 'effectiveDate', label: i18n.t('effectiveDate', { ns: 'survey' }) },
] as const;

type QuestionFieldType = (typeof questionFields)[number]['value'];

export const getQuestionListInfo = (
  data: any,
  survey: ISurveyViewWithData,
  uniqueQuestionIdList: string[] = []
) => {
  const uniqueQuestionName: string[] = [];
  let uniqueIdList = null;

  if (uniqueQuestionIdList.length) {
    uniqueIdList = uniqueQuestionIdList;
    uniqueQuestionIdList.map((qId: string) => {
      const foundedQuestion = findQuestionById(
        qId?.includes('@') ? (qId.split('@').at(0) ?? '') : (qId ?? ''),
        survey
      );
      if (foundedQuestion) {
        uniqueQuestionName.push(
          foundedQuestion?.title +
            (qId?.includes('@')
              ? ` (${i18n.t('score', { ns: 'survey' })})`
              : '')
        );
      } else {
        const foundedQuestionField = questionFields.find(
          (el) => el.value === qId
        );
        if (foundedQuestionField) {
          uniqueQuestionName.push(foundedQuestionField.label);
        }
      }
    });
  } else {
    uniqueIdList = data.reduce(
      (arr: number[], response: ISurveyResponseView) => {
        response.surveyAnswerList?.map((answer: IAnswersView) => {
          if (!arr.includes(answer.questionId)) {
            const foundedQuestion = (
              (survey.data as any) || {}
            )?.surveyQuestionList?.find(
              (question: any) => question.id === answer.questionId
            );
            if (foundedQuestion) {
              arr.push(answer.questionId);
              uniqueQuestionName.push(foundedQuestion?.title);
            }
          }
        });
        return arr;
      },
      []
    );
  }
  return [uniqueQuestionName, uniqueIdList];
};

export const findQuestionById = (
  qId: string,
  survey: ISurveyViewWithData
): IQuestionsView => {
  const foundedQuestion = (
    (survey?.data as any) || {}
  )?.surveyQuestionList?.find((question: any) => question.id === Number(qId));
  return foundedQuestion;
};

export const getMultipleChoiceQuestionInfo = (
  answer: IMultipleChoiceAnswerView,
  question: IMultipleChoiceQuestionView,
  tags: ITags,
  hasScore: boolean = false
) => {
  let result = '';
  if (hasScore) {
    result = answer?.score ? convertNumbersToProperFormat(answer?.score) : '';
  } else {
    if (!question.useTags) {
      (answer?.values?.length
        ? answer.values
        : answer?.value
          ? [answer.value]
          : []
      ).forEach((value: string) => {
        const choiceIndex = question.choiceList.findIndex(
          (item: IChoiceView) => item.value === value
        );
        result =
          (result ?? '') +
          (result ? ', ' : '') +
          (question.choiceList[choiceIndex]?.text ?? '');
      });
    } else {
      answer.selected.forEach((tagId: number) => {
        result =
          (result ?? '') + (result ? ', ' : '') + (tags.data[tagId].text ?? '');
      });
    }
  }
  return result;
};

const parseNumericValue = (value: string | null | undefined) => {
  if (value === null || value === undefined || value.trim() === '') {
    return undefined;
  }
  const parsed = Number(englishNumber(value));
  return isANumber(parsed) ? parsed : undefined;
};

export const formatTextAnswer = (
  value: string | null | undefined,
  inputType: ITextQuestionView['inputType']
): ReactNode => {
  if (!value) {
    return '';
  }
  switch (inputType) {
    case 'number': {
      const parsed = parseNumericValue(value);
      return parsed === undefined
        ? value
        : convertNumbersToProperFormat(parsed);
    }
    case 'digits':
    case 'phone':
    case 'inid':
      return <span dir="ltr">{convertDigitToProperFormat(value)}</span>;
    case 'date':
    case 'dateAndTime': {
      const timestamp = parseNumericValue(value);
      if (!timestamp || timestamp <= 0) {
        return '';
      }
      return smartDateString(timestamp, inputType === 'dateAndTime', true);
    }
    case 'duration': {
      const hours = parseNumericValue(value);
      return hours === undefined ? '' : convertTimeToString(hours);
    }
    case 'map': {
      const [lat, lng] = value.split(',').map(Number);
      if (!isANumber(lat) || !isANumber(lng)) {
        return value;
      }
      return (
        <a
          href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`}
          target="_blank"
          rel="noopener noreferrer"
          dir="ltr"
        >
          {convertDigitToProperFormat(`${lat.toFixed(5)}, ${lng.toFixed(5)}`)}
        </a>
      );
    }
    case 'rich':
      return (getPureTextFromHtml(value) ?? '').trim();
    case 'area':
      return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
    case 'none':
      return '';
    default:
      return value;
  }
};

const getBaseQuestionId = (qId: string) =>
  qId.includes('@') ? (qId.split('@').at(0) ?? qId) : qId;

const compareSortValues = (a: number | string, b: number | string) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const getMultipleChoiceSortValue = (
  answer: IMultipleChoiceAnswerView | undefined,
  question: IMultipleChoiceQuestionView,
  tags: ITags,
  hasScore: boolean
): number | string => {
  if (!answer) {
    return hasScore ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }
  if (hasScore) {
    return answer.score ?? Number.NEGATIVE_INFINITY;
  }
  if (question.useTags) {
    if (!answer.selected?.length) {
      return Number.POSITIVE_INFINITY;
    }
    return Math.min(
      ...answer.selected.map(
        (tagId) => tags.data[tagId]?.order ?? Number.POSITIVE_INFINITY
      )
    );
  }
  const values = answer.values?.length
    ? answer.values
    : answer.value
      ? [answer.value]
      : [];
  if (!values.length) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.min(
    ...values.map((value) => {
      const choiceIndex = question.choiceList.findIndex(
        (item) => item.value === value
      );
      return choiceIndex >= 0 ? choiceIndex : Number.POSITIVE_INFINITY;
    })
  );
};

const getSortValue = (
  qId: string,
  response: ISurveyResponseView,
  survey: ISurveyViewWithData,
  tags: ITags,
  users: RootState['users']['map']
): number | string => {
  const foundedQuestionField = questionFields.find((el) => el.value === qId);
  if (foundedQuestionField) {
    switch (foundedQuestionField.value) {
      case 'submitDate':
        return response.submitDate ?? Number.NEGATIVE_INFINITY;
      case 'effectiveDate':
        return response.effectiveDate ?? Number.NEGATIVE_INFINITY;
      case 'score':
        return isANumber(response.score)
          ? Number(response.score)
          : Number.NEGATIVE_INFINITY;
      case 'relatedUser':
        return (
          (response.instance ? users?.[response.instance]?.name : '') ?? ''
        );
      case 'userId':
        return (response.userId ? users?.[response.userId]?.name : '') ?? '';
    }
  }

  const baseQuestionId = getBaseQuestionId(qId);
  const hasScore = qId.includes('@');
  const foundedQuestion = findQuestionById(baseQuestionId, survey);
  const foundedAnswer = response.surveyAnswerList.find(
    (el) => el.questionId?.toString() === baseQuestionId
  );

  if (!foundedQuestion) {
    return '';
  }

  if (foundedQuestion.type === 'multipleChoice') {
    return getMultipleChoiceSortValue(
      foundedAnswer as IMultipleChoiceAnswerView,
      foundedQuestion,
      tags,
      hasScore
    );
  }

  if (foundedQuestion.type === 'text') {
    const value = (foundedAnswer as ITextAnswerView | undefined)?.value ?? '';
    switch (foundedQuestion.inputType) {
      case 'number':
      case 'digits':
      case 'duration':
      case 'date':
      case 'dateAndTime':
        return parseNumericValue(value) ?? Number.NEGATIVE_INFINITY;
      case 'rich':
        return (getPureTextFromHtml(value) ?? '').trim().toLowerCase();
      default:
        return value.toLowerCase();
    }
  }

  if (foundedQuestion.type === 'indicator') {
    const indicatorAnswer = foundedAnswer as IIndicatorAnswerView | undefined;
    if (!indicatorAnswer) {
      return Number.NEGATIVE_INFINITY;
    }
    const hasDenominator = !!(foundedQuestion as IIndicatorQuestionView)
      .denominator;
    if (hasDenominator && indicatorAnswer.denominator) {
      return indicatorAnswer.numerator / indicatorAnswer.denominator;
    }
    return indicatorAnswer.numerator;
  }

  return (
    (foundedAnswer as ITextAnswerView | undefined)?.value?.toLowerCase() ?? ''
  );
};

const QuestionListReport: FC<IQuestionListReportProps> = ({
  data,
  survey,
  questionIdList,
  tags,
}) => {
  const { t } = useTranslation('survey');

  const users = useSelector((state: RootState) => state.users.map);
  const providers = useSelector((state: RootState) => state.providers);

  const [uniqueQuestionName, _] = getQuestionListInfo(
    data,
    survey,
    questionIdList
  );

  const local = survey.data.id
    ? providers.map?.[survey.data.id]?.data?.surveyOwnerProperties.localId ||
      getCalendarLocal()
    : getCalendarLocal();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [foundedAnswerModal, setFoundedAnswerModal] = useState<
    IAnswersView | undefined
  >(undefined);

  const [enabledSortColumnId, setEnabledSortColumnId] = useState<
    string | undefined
  >(() => questionIdList[0]);
  const [hoveredColumnIndex, setHoveredColumnIndex] = useState<
    number | undefined
  >();

  const handleToggleSorting = (columnId: string) => {
    setEnabledSortColumnId((prev) =>
      prev === columnId ? undefined : columnId
    );
  };

  const sortedData = useMemo(() => {
    if (!enabledSortColumnId) {
      return data;
    }
    return [...data].sort((a, b) =>
      compareSortValues(
        getSortValue(enabledSortColumnId, a, survey, tags, users),
        getSortValue(enabledSortColumnId, b, survey, tags, users)
      )
    );
  }, [data, enabledSortColumnId, survey, tags, users]);

  const generateDescription = (description: any) => {
    return (
      <APopover
        target={
          <div style={{ margin: '0' }}>
            <HoverIcon color="blue" type="info" />
          </div>
        }
        trigger="click"
        placement="bottom"
      >
        <div>
          <CKEditorField
            data={description}
            // disabled={true}
            dir="rtl"
            config={CKEditorReadOnlyConfig}
          />
        </div>
      </APopover>
    );
  };

  const getUsersAnswerInfo = (
    field: QuestionFieldType,
    response: ISurveyResponseView
  ) => {
    if (field === 'effectiveDate') {
      const version = survey.data.versions[response.surveyVersion];
      const periodType = getTimeScale(version.periodType);
      return response?.effectiveDate
        ? getEffectiveName(
            periodType as TimeScale,
            response.effectiveDate,
            local
          )
        : '';
    } else if (field === 'relatedUser') {
      return response?.instance ? users?.[response.instance]?.name : '';
    } else if (field === 'score') {
      return isANumber(response?.score)
        ? convertNumbersToProperFormat(Number(response.score), 2)
        : '';
    } else if (field === 'submitDate') {
      return response?.submitDate
        ? smartDateString(response?.submitDate, true, true, true)
        : '';
    } else {
      return response?.userId ? users?.[response.userId]?.name : '';
    }
  };

  return (
    <>
      {foundedAnswerModal && (foundedAnswerModal as IFileAnswerView)?.file ? (
        <TaskAttachmentsModal
          isActive={isModalOpen && foundedAnswerModal ? true : false}
          attachments={[
            {
              document: (foundedAnswerModal as IFileAnswerView)?.file,
              id: -1,
              taskId: -1,
              deleted: false,
              senderId: 0,
            },
          ]}
          selectedIndex={0}
          users={undefined}
          onIndexChanged={undefined}
          onClose={() => {
            setIsModalOpen(false);
            setFoundedAnswerModal(undefined);
          }}
        />
      ) : null}
      <table className="highlightedTable align-center-table">
        <thead>
          <tr>
            {uniqueQuestionName.map((name: string, index: number) => {
              const columnId = questionIdList[index];
              const isSortActive = columnId === enabledSortColumnId;
              const showSortIcon = hoveredColumnIndex === index || isSortActive;

              return (
                <th
                  key={`${name}-${index}`}
                  className="relative"
                  onMouseEnter={() => setHoveredColumnIndex(index)}
                  onMouseLeave={() => setHoveredColumnIndex(undefined)}
                  onClick={() => handleToggleSorting(columnId)}
                  style={{ cursor: 'pointer', position: 'sticky', top: 0 }}
                >
                  <div>{name}</div>
                  {showSortIcon ? (
                    <div
                      className="absolute"
                      style={{
                        cursor: 'pointer',
                        left: '5px',
                        top: '50%',
                        transform: 'translate(0, -50%)',
                        padding: '2px',
                        borderRadius: '50%',
                        height: '2rem',
                        width: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: blue,
                      }}
                    >
                      {withTitle(
                        <Icon
                          type="arrow-up-circle"
                          color={isSortActive ? exactWhite : black}
                        />,
                        t('sortBasedOn', { name })
                      )}
                    </div>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {sortedData.map((response: ISurveyResponseView) => {
            return (
              <tr key={response.id}>
                {questionIdList.map((qId: string) => {
                  const foundedAnswer = response.surveyAnswerList.find(
                    (el) => el.questionId?.toString() === qId
                  );

                  const foundedQuestion = findQuestionById(
                    qId?.includes('@')
                      ? (qId.split('@').at(0) ?? '')
                      : (qId ?? ''),
                    survey
                  );
                  const foundedQuestionField = questionFields?.find(
                    (el) => el.value === qId
                  );

                  if (!foundedQuestion && foundedQuestionField) {
                    const result = getUsersAnswerInfo(
                      foundedQuestionField.value,
                      response
                    );
                    return <td key={`${Math.random()}`}>{result}</td>;
                  }

                  if (
                    foundedQuestion &&
                    foundedQuestion?.type === 'multipleChoice'
                  ) {
                    const multipleChoiceQuestionInfo =
                      getMultipleChoiceQuestionInfo(
                        foundedAnswer as IMultipleChoiceAnswerView,
                        foundedQuestion,
                        tags,
                        qId?.includes('@')
                      );
                    return (
                      <td key={`${Math.random()}`}>
                        <div className="flex items-center flex-2">
                          <div style={{ margin: '0 0.5rem' }}>
                            {multipleChoiceQuestionInfo || ''}
                          </div>
                          {foundedAnswer?.description &&
                            generateDescription(foundedAnswer.description)}
                        </div>
                      </td>
                    );
                  } else if (foundedQuestion?.type === 'file') {
                    return (
                      <td
                        key={`${Math.random()}`}
                        className="flex items-center justify-center"
                      >
                        {(foundedAnswer as IFileAnswerView)?.file
                          ?.thumbnailUrl ? (
                          <img
                            style={{
                              height: '2.5rem',
                              width: '2.5rem',
                              borderRadius: '0.5rem',
                              margin: '0 0.5rem',
                            }}
                            src={`${(foundedAnswer as IFileAnswerView)?.file?.thumbnailUrl}`}
                            onClick={() => {
                              setIsModalOpen(true);
                              setFoundedAnswerModal(foundedAnswer);
                            }}
                          />
                        ) : (foundedAnswer as IFileAnswerView)?.file ? (
                          <LocalIcon
                            iconName={checkFileFormat(
                              (foundedAnswer as IFileAnswerView)?.file
                                ?.properties.format
                            )}
                            color={black}
                            onClick={() => {
                              setIsModalOpen(true);
                              setFoundedAnswerModal(foundedAnswer);
                            }}
                            style={{ margin: '0 0.5rem' }}
                          />
                        ) : (
                          <></>
                        )}
                        {foundedAnswer?.description &&
                          generateDescription(foundedAnswer.description)}
                      </td>
                    );
                  } else if (foundedQuestion?.type === 'indicator') {
                    const hasDenominator = !!(
                      foundedQuestion as IIndicatorQuestionView
                    )?.denominator;
                    return (
                      <td key={`${Math.random()}`}>
                        <div className="flex items-center flex-2">
                          <div>
                            {hasDenominator
                              ? `${convertNumbersToProperFormat((foundedAnswer as IIndicatorAnswerView)?.numerator)}/${convertNumbersToProperFormat((foundedAnswer as IIndicatorAnswerView)?.denominator)}`
                              : `${convertNumbersToProperFormat((foundedAnswer as IIndicatorAnswerView)?.numerator)}`}
                          </div>
                          {foundedAnswer?.description &&
                            generateDescription(foundedAnswer.description)}
                        </div>
                      </td>
                    );
                  } else {
                    const answerValue = (foundedAnswer as ITextAnswerView)
                      ?.value;

                    return (
                      <td key={`${Math.random()}`}>
                        <div>
                          {foundedQuestion?.type === 'text' ? (
                            formatTextAnswer(
                              answerValue,
                              foundedQuestion.inputType
                            )
                          ) : (
                            <SanitizedHTML
                              allowedAttributes={{ b: ['style'] }}
                              allowedTags={['span']}
                              html={answerValue?.toLowerCase() || ''}
                            />
                          )}
                        </div>
                        {foundedAnswer?.description &&
                          generateDescription(foundedAnswer.description)}
                      </td>
                    );
                  }
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default QuestionListReport;
